import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Select, Table, Tag } from 'antd';
import { FiLink, FiRefreshCcw, FiTrendingUp, FiUserCheck, FiUsers } from 'react-icons/fi';
import dayjs from 'dayjs';
import AppRangePicker from '@/components/common/AppRangePicker';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import StatCard from '../../components/StatCard/StatCard';
import raReferralService from '../../services/raReferralService';
import orderService from '../../services/orderService';
import { buildRaReferralQueryParams } from './raReferrals.helpers';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatInt(value) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(toNumber(value));
}

function formatAmount(value) {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toNumber(value));
}

function formatLabel(value) {
  if (!value) return '—';
  return String(value)
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

function getInvestorReferralColumns({ goToPartnerProfile }) {
  const partnerLinkButton = (name, onClick) => (
    <button
      type="button"
      className="font-medium text-blue-700 hover:text-blue-800 hover:underline"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {name || '—'}
    </button>
  );

  return [
    {
      title: 'Investor',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (name) => <span className="font-medium text-gray-900">{name || '—'}</span>,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (value) => <span className="text-gray-700">{value || '—'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value) => <span className="text-gray-600">{value || '—'}</span>,
    },
    {
      title: 'Partner',
      dataIndex: 'partner_name',
      key: 'partner_name',
      render: (name, record) =>
        record?.partner_uuid
          ? partnerLinkButton(name, () => goToPartnerProfile(record))
          : <span className="text-gray-600">{name || '—'}</span>,
    },
    {
      title: 'RM name',
      dataIndex: 'rm_name',
      key: 'rm_name',
      render: (value) => <span className="text-gray-700">{value || '—'}</span>,
    },
    {
      title: 'Referred At',
      dataIndex: 'referred_at',
      key: 'referred_at',
      render: (value) => (value ? dayjs(value).format('DD-MMM-YYYY HH:mm') : '—'),
    },
    {
      title: 'Source',
      dataIndex: 'acquisition_source',
      key: 'acquisition_source',
      render: (value) => <Tag>{formatLabel(value)}</Tag>,
    },
    {
      title: 'Captured From',
      dataIndex: 'captured_from',
      key: 'captured_from',
      render: (value) => <span className="capitalize text-gray-600">{value || '—'}</span>,
    },
    {
      title: 'Subscribed',
      dataIndex: 'has_subscription',
      key: 'has_subscription',
      render: (value) => (
        <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (value) => <span className="font-semibold text-emerald-700">{formatAmount(value)}</span>,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? dayjs(value).format('DD-MMM-YYYY HH:mm') : '—'),
    },
  ];
}

function getPartnerSummaryColumns() {
  return [
    {
      title: 'Partner',
      dataIndex: 'partner_name',
      key: 'partner_name',
      render: (value) => <span className="font-medium text-gray-900">{value || '—'}</span>,
    },
    {
      title: 'RM name',
      dataIndex: 'rm_name',
      key: 'rm_name',
      render: (value) => <span className="text-gray-700">{value || '—'}</span>,
    },
    {
      title: 'Total Referred',
      dataIndex: 'total_referred',
      key: 'total_referred',
      align: 'right',
      render: (value) => <span className="font-semibold text-blue-700">{formatInt(value)}</span>,
    },
    {
      title: 'Subscribed',
      dataIndex: 'subscribed_count',
      key: 'subscribed_count',
      align: 'right',
      render: (value) => <span className="font-semibold text-green-700">{formatInt(value)}</span>,
    },
    {
      title: 'Total Revenue',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      align: 'right',
      render: (value) => <span className="font-semibold text-emerald-700">{formatAmount(value)}</span>,
    },
  ];
}

export default function RAReferralsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [partnerRows, setPartnerRows] = useState([]);
  const [summary, setSummary] = useState({
    total_referred: 0,
    subscribed_count: 0,
    total_revenue: 0,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [dateRange, setDateRange] = useState(null);
  const [rmUuid, setRmUuid] = useState(null);
  const [rmOptions, setRmOptions] = useState([]);
  const [loadingRms, setLoadingRms] = useState(false);

  const fetchReferrals = useCallback(async ({ page = 1, limit = 20, range = null, rm = null } = {}) => {
    setLoading(true);
    setError('');
    try {
      const data = await raReferralService.getInvestorReferrals(
        buildRaReferralQueryParams({ page, limit, range, rmUuid: rm }),
      );
      const items = Array.isArray(data?.items) ? data.items : [];
      const nextPagination = data?.pagination || {};
      const nextSummary = data?.summary || {};

      setRows(items);
      setPartnerRows(Array.isArray(nextSummary.partners) ? nextSummary.partners : []);
      setSummary({
        total_referred: toNumber(nextSummary.total_referred),
        subscribed_count: toNumber(nextSummary.subscribed_count),
        total_revenue: toNumber(nextSummary.total_revenue),
      });
      setPagination({
        current: toNumber(nextPagination.page) || page,
        pageSize: toNumber(nextPagination.limit) || limit,
        total: toNumber(nextPagination.total) || items.length,
      });
    } catch (e) {
      setRows([]);
      setPartnerRows([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch RA referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals({ page: 1, limit: pagination.pageSize });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setLoadingRms(true);
    orderService
      .listOrderRms()
      .then((rms) => {
        if (cancelled) return;
        setRmOptions(
          (Array.isArray(rms) ? rms : [])
            .map((rm) => {
              const value = rm?.uuid ?? rm?.id;
              if (!value) return null;
              const label = rm?.name ? String(rm.name).trim() : 'RM';
              return { value: String(value), label };
            })
            .filter(Boolean),
        );
      })
      .catch(() => {
        if (!cancelled) setRmOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRms(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dateRangeRef = useRef(dateRange);
  const rmUuidRef = useRef(rmUuid);
  const paginationRef = useRef(pagination);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);
  useEffect(() => {
    rmUuidRef.current = rmUuid;
  }, [rmUuid]);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const handleDateChange = useCallback((dates) => {
    setDateRange(dates);
    if (dates && (!dates[0] || !dates[1])) return;
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchReferralsRef.current({
      page: 1,
      limit: paginationRef.current.pageSize,
      range: dates,
      rm: rmUuidRef.current,
    });
  }, []);

  const handleRmChange = useCallback((value) => {
    setRmUuid(value || null);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchReferralsRef.current({
      page: 1,
      limit: paginationRef.current.pageSize,
      range: dateRangeRef.current,
      rm: value || null,
    });
  }, []);

  const handleClearDateFilter = useCallback(() => {
    setDateRange(null);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchReferralsRef.current({
      page: 1,
      limit: paginationRef.current.pageSize,
      range: null,
      rm: rmUuidRef.current,
    });
  }, []);

  const goToPartnerProfile = useCallback(
    (record) => {
      if (!record?.partner_uuid) return;
      navigate(`/partner/details/${encodeURIComponent(String(record.partner_uuid))}`);
    },
    [navigate],
  );

  const columns = useMemo(
    () => getInvestorReferralColumns({ goToPartnerProfile }),
    [goToPartnerProfile],
  );

  const partnerColumns = useMemo(() => getPartnerSummaryColumns(), []);

  const fetchReferralsRef = useRef(fetchReferrals);
  useEffect(() => {
    fetchReferralsRef.current = fetchReferrals;
  }, [fetchReferrals]);

  const handleTableChange = useCallback((nextPagination) => {
    fetchReferralsRef.current({
      page: nextPagination.current,
      limit: nextPagination.pageSize,
      range: dateRangeRef.current,
      rm: rmUuidRef.current,
    });
  }, []);

  const handleRefresh = useCallback(() => {
    const currentPagination = paginationRef.current;
    fetchReferralsRef.current({
      page: currentPagination.current,
      limit: currentPagination.pageSize,
      range: dateRangeRef.current,
      rm: rmUuidRef.current,
    });
  }, []);

  return (
    <DashboardPageWrapper>
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RA Referrals</h1>
          <p className="mt-1 text-sm text-gray-500">
            Investors referred through RA partner referral links, with subscription and revenue summary.
          </p>
        </div>
        <Button
          icon={<FiRefreshCcw />}
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            Referred Date Range
          </label>
          <AppRangePicker
            value={dateRange}
            onChange={handleDateChange}
            allowClear
            className="h-[38px] w-full min-w-[260px] rounded-lg sm:w-auto"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            RM
          </label>
          <Select
            allowClear
            showSearch
            placeholder="All RMs"
            loading={loadingRms}
            value={rmUuid}
            onChange={handleRmChange}
            optionFilterProp="label"
            options={rmOptions}
            className="h-[38px] min-w-[220px]"
          />
        </div>
        {dateRange?.[0] && dateRange?.[1] ? (
          <Button type="link" onClick={handleClearDateFilter} className="px-0">
            Clear date filter
          </Button>
        ) : null}
      </div>

      {error ? (
        <Alert type="error" showIcon className="mb-4" message="Unable to load RA referrals" description={error} />
      ) : null}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Referred"
          value={formatInt(summary.total_referred)}
          icon={<FiUsers />}
          cardClass="border-blue-100 bg-blue-50/40"
          valueClass="text-blue-700"
        />
        <StatCard
          label="Subscribed"
          value={formatInt(summary.subscribed_count)}
          icon={<FiUserCheck />}
          cardClass="border-green-100 bg-green-50/40"
          valueClass="text-green-700"
        />
        <StatCard
          label="Total Revenue"
          value={formatAmount(summary.total_revenue)}
          icon={<FiTrendingUp />}
          cardClass="border-emerald-100 bg-emerald-50/40"
          valueClass="text-emerald-700"
        />
        <StatCard
          label="Partners"
          value={formatInt(partnerRows.length)}
          icon={<FiLink />}
          cardClass="border-purple-100 bg-purple-50/40"
          valueClass="text-purple-700"
        />
      </div>

      {partnerRows.length > 0 ? (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Partner Summary</h2>
          </div>
          <Table
            columns={partnerColumns}
            dataSource={partnerRows}
            rowKey={(record) => record.partner_uuid || record.partner_name}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
            locale={{ emptyText: 'No partner summary available' }}
          />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Referred Investors</h2>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey={(record) => record.uuid || `${record.email}-${record.created_at}`}
          loading={loading}
          scroll={{ x: 'max-content' }}
          className="min-w-[900px] [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          onChange={handleTableChange}
          locale={{ emptyText: 'No referred investors found' }}
        />
      </div>
    </DashboardPageWrapper>
  );
}
